import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api, { toErrMsg } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import PasswordStrengthMeter, { scorePassword } from "@/components/PasswordStrengthMeter";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { t } = useI18n();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) { setError("Missing token"); return; }
    api.get(`/invites/lookup/${token}`)
      .then((r) => setInvite(r.data))
      .catch((e) => setError(toErrMsg(e, "Invalid or expired invite")));
  }, [token]);

  const accept = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (scorePassword(password) < 2) { toast.error("Password too weak. Mix upper + lower + digit + symbol."); return; }
    setLoading(true);
    try {
      const r = await api.post("/invites/accept", { token, name, password });
      localStorage.setItem("af_token", r.data.token);
      toast.success("Welcome to the team");
      window.location.href = "/";
    } catch (err) {
      toast.error(toErrMsg(err, "Failed"));
    } finally { setLoading(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md rounded-lg border bg-white p-8 shadow-sm">
        <div className="mb-6 flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-emerald-600" />
          <h2 className="text-xl font-semibold">{t("invite.acceptTitle")}</h2>
        </div>
        {error && <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>}
        {!invite && !error && <div className="text-sm text-muted-foreground">{t("common.loading")}</div>}
        {invite && (
          <>
            <div className="mb-4 rounded-md border bg-slate-50 p-3 text-sm">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Invited</div>
              <div className="mt-1 font-medium text-slate-900" data-testid="invite-shown-email">{invite.email}</div>
              <div className="text-xs text-muted-foreground">
                {t("invite.description1")} <span className="font-medium text-slate-900">{invite.org_name}</span> {t("invite.description2")} <span className="font-medium text-slate-900">{invite.role}</span>
              </div>
            </div>
            <form onSubmit={accept} className="space-y-3">
              <div>
                <Label>{t("auth.email")}</Label>
                <Input value={invite.email} disabled data-testid="invite-email-input" />
              </div>
              <div>
                <Label>{t("auth.name")}</Label>
                <Input data-testid="invite-name" value={name} onChange={(e) => setName(e.target.value)} required />
              </div>
              <div>
                <Label>{t("auth.password")}</Label>
                <Input type="password" data-testid="invite-password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} autoComplete="new-password" />
                <PasswordStrengthMeter password={password} testid="invite-pwd-strength" />
              </div>
              <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" disabled={loading} data-testid="invite-accept">
                {loading ? t("common.loading") : t("invite.accept")}
              </Button>
            </form>
          </>
        )}
        <p className="mt-4 text-center text-sm">
          <Link to="/login" className="text-emerald-600 hover:underline">{t("auth.haveAccount")} {t("auth.login")}</Link>
        </p>
      </div>
    </div>
  );
}
